import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import useQueryParam from "../../../hooks/useQueryParam";

import {
  validateSetupToken,
  startAccountSetup,
  verifySetupMfa,
} from "../../../redux/features/auth/accountSetupSlice";

import Button from "../../../components/ui/button/Button";
import Alert from "../../../components/ui/alert/Alert";
import PasswordSetupForm from "../../../components/auth/setup/PasswordSetupForm";
import MfaVerifyForm from "../../../components/auth/setup/MfaVerifyForm";

import "./SetupAccountPage.css";

export default function SetupAccountPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = useQueryParam("token");

  const [step, setStep] = useState("validate");

  const { tokenStatus, startStatus, mfaStatus, inviteData, mfaData, error } =
    useSelector((state) => state.accountSetup);

  useEffect(() => {
    if (token) {
      dispatch(validateSetupToken(token));
    }
  }, [dispatch, token]);

  useEffect(() => {
    if (tokenStatus === "valid") {
      setStep("validate");
    }

    if (tokenStatus === "invalid") {
      setStep("validate");
    }
  }, [tokenStatus]);

  useEffect(() => {
    if (startStatus === "success") {
      setStep("mfa");
    }
  }, [startStatus]);

  useEffect(() => {
    if (mfaStatus === "success") {
      setStep("success");
    }
  }, [mfaStatus]);

  const handleStartSetup = ({ password, confirmPassword }) => {
    if (!token) return;

    dispatch(
      startAccountSetup({
        token,
        password,
        confirmPassword,
      }),
    );
  };

  const handleVerifyMfa = (code) => {
    if (!token) return;

    dispatch(
      verifySetupMfa({
        token,
        code,
      }),
    );
  };

  if (!token) {
    return (
      <div className="setup-card">
        <h2>Ongeldige link</h2>

        <Alert type="error">
          Deze uitnodigingslink bevat geen geldige token.
        </Alert>

        <Link to="/login" className="setup-card__link">
          Terug naar login
        </Link>
      </div>
    );
  }

  return (
    <div className="setup-card">
      <div className="setup-card__header">
        <span>Account setup</span>

        {step === "validate" && <h2>Uitnodiging controleren</h2>}
        {step === "password" && <h2>Wachtwoord instellen</h2>}
        {step === "mfa" && <h2>MFA activeren</h2>}
        {step === "success" && <h2>Account geactiveerd</h2>}

        {step === "validate" && (
          <p>
            We controleren eerst of je uitnodiging geldig is voordat je een
            wachtwoord en MFA instelt.
          </p>
        )}

        {step === "password" && (
          <p>
            Kies een sterk wachtwoord. Daarna koppel je je account aan een
            MFA-app.
          </p>
        )}

        {step === "mfa" && (
          <p>Scan de QR-code en bevestig je account met je MFA-code.</p>
        )}

        {step === "success" && (
          <p>Je account is actief. Je kan nu veilig inloggen.</p>
        )}
      </div>

      {step === "validate" && (
        <>
          {tokenStatus === "idle" && <Alert type="info">Voorbereiden...</Alert>}

          {tokenStatus === "loading" && (
            <Alert type="info">Uitnodiging wordt gecontroleerd...</Alert>
          )}

          {tokenStatus === "invalid" && (
            <>
              <Alert type="error">{error}</Alert>

              <Link to="/login" className="setup-card__link">
                Terug naar login
              </Link>
            </>
          )}

          {tokenStatus === "valid" && (
            <>
              <Alert type="success">
                Uitnodiging is geldig
                {inviteData?.email ? ` voor ${inviteData.email}` : "."}
              </Alert>

              <Button onClick={() => setStep("password")}>
                Volgende stap: wachtwoord instellen
              </Button>
            </>
          )}
        </>
      )}

      {step === "password" && (
        <PasswordSetupForm
          onSubmit={handleStartSetup}
          isLoading={startStatus === "loading"}
          serverError={startStatus === "error" ? error : ""}
        />
      )}

      {step === "mfa" && (
        <MfaVerifyForm
          qrCode={mfaData?.qr_code}
          onSubmit={handleVerifyMfa}
          isLoading={mfaStatus === "loading"}
          serverError={mfaStatus === "error" ? error : ""}
        />
      )}

      {step === "success" && (
        <div className="setup-success">
          <Alert type="success">
            Je POWER-account is succesvol geactiveerd.
          </Alert>

          <Button onClick={() => navigate("/login")}>Naar login</Button>
        </div>
      )}
    </div>
  );
}
